import { GENRES, MODES, makeSettings } from "@/lib/mastering";

const Knob = ({ label, value, min, max, step, unit, onChange, testid, fmt }) => (
  <label className="block space-y-1.5">
    <span className="flex justify-between font-mono-tga text-[10px] uppercase tracking-[0.2em] text-zinc-400">
      <span>{label}</span><span className="text-emerald-300">{fmt ? fmt(value) : `${value > 0 && unit === "dB" ? "+" : ""}${Number(value).toFixed(step < 1 ? 1 : 0)} ${unit}`}</span>
    </span>
    <input type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(parseFloat(e.target.value))}
      className="w-full accent-emerald-400 cursor-pointer" data-testid={testid} />
  </label>
);

const Group = ({ title, children }) => (
  <div className="rounded-xl border border-white/10 bg-white/5 p-5 space-y-5">
    <p className="font-display text-xs font-bold uppercase tracking-[0.2em] text-sky-300">{title}</p>
    {children}
  </div>
);

const Tile = ({ active, onClick, label, sub, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`text-left rounded-xl border p-3 transition-[border-color,background-color,transform] hover:-translate-y-0.5 ${active ? "border-emerald-400 bg-emerald-400/10" : "border-white/15 hover:border-white/40"}`}>
    <p className="font-display text-sm font-bold uppercase">{label}</p>
    <p className="text-[11px] text-zinc-400 mt-1">{sub}</p>
  </button>
);

export const MasteringControls = ({ s, setS, genre, setGenre, mode, setMode }) => {
  const set = (k) => (v) => { setS({ ...s, [k]: v }); setGenre("custom"); };
  const pickGenre = (g) => { setGenre(g); setS({ ...makeSettings(g, mode), eqMatch: s.eqMatch }); };
  const pickMode = (m) => { setMode(m); setS(genre in GENRES ? { ...makeSettings(genre, m), eqMatch: s.eqMatch } : { ...s, ...MODES[m] }); };
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-2 max-w-xl">
        {Object.entries(MODES).map(([k, m]) => <Tile key={k} active={mode === k} onClick={() => pickMode(k)} label={m.label} sub={m.sub} testid={`mastering-mode-${k}`} />)}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
        {Object.entries(GENRES).map(([k, g]) => <Tile key={k} active={genre === k} onClick={() => pickGenre(k)} label={g.label} sub={g.sub} testid={`mastering-preset-${k}`} />)}
      </div>
      <div className="grid lg:grid-cols-3 gap-4">
        <Group title="Equalizzatore">
          <Knob label="Bassi (90 Hz)" value={s.low} min={-6} max={6} step={0.5} unit="dB" onChange={set("low")} testid="mastering-low" />
          <Knob label="Pulizia medio-bassi (280 Hz)" value={s.lowMid} min={-6} max={3} step={0.5} unit="dB" onChange={set("lowMid")} testid="mastering-lowmid" />
          <Knob label="Presenza (3 kHz)" value={s.mid} min={-6} max={6} step={0.5} unit="dB" onChange={set("mid")} testid="mastering-mid" />
          <Knob label="Aria (11 kHz)" value={s.high} min={-6} max={6} step={0.5} unit="dB" onChange={set("high")} testid="mastering-high" />
        </Group>
        <Group title="Dinamica e colore">
          <Knob label="Compressione multibanda" value={s.comp} min={0} max={10} step={0.5} unit="" onChange={set("comp")} testid="mastering-comp" />
          <Knob label="Punch cassa e basso" value={s.punch} min={0} max={10} step={0.5} unit="" onChange={set("punch")} testid="mastering-punch" />
          <Knob label="Saturazione analogica" value={s.sat} min={0} max={10} step={0.5} unit="" onChange={set("sat")} testid="mastering-sat" />
          <div className="inline-flex rounded-full border border-white/20 p-1" role="group">
            {[["tape", "Nastro"], ["tube", "Valvola"]].map(([k, l]) => (
              <button key={k} type="button" onClick={() => set("satType")(k)} data-testid={`mastering-sat-${k}`}
                className={`rounded-full px-4 py-1 text-xs font-bold ${s.satType === k ? "bg-amber-400 text-[#151827]" : "text-zinc-300"}`}>{l}</button>
            ))}
          </div>
        </Group>
        <Group title="Stereo e volume">
          <Knob label="Ampiezza stereo" value={s.width * 100} min={70} max={150} step={1} unit="%" onChange={(v) => set("width")(v / 100)} testid="mastering-width" />
          <Knob label="Bassi mono sotto" value={s.mono} min={40} max={250} step={5} unit="Hz" onChange={set("mono")} testid="mastering-mono" />
          <Knob label="Volume finale (target)" value={s.target} min={-18} max={-6} step={0.5} unit="LUFS" onChange={set("target")} testid="mastering-target" />
          <Knob label="True peak massimo" value={s.ceiling} min={-3} max={-0.1} step={0.1} unit="dBTP" onChange={set("ceiling")} testid="mastering-ceiling" />
        </Group>
      </div>
    </div>
  );
};
